import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { captureException } from '@sentry/react';
import { useState } from 'react';

import { getDisplayError } from '@utils/get-display-error';
import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { InputField } from 'components/input/InputField';
import { TrailerCostPayee } from 'src/types/enums';
import { SimpleSelectField } from 'components/select/SimpleSelectField';
import { PAID_BY_OPTIONS } from './constants';
import { decimateAndFormatNumber, parseNumberInput } from '@utils/number';
import { useTrailer } from '../trailerContext';
import { invariant, nullthrows } from '@utils/invariant';
import { CustomerComboboxField, Customer } from 'src/app/customer/CustomerComboboxField';
import { SupplierComboboxField, Supplier } from 'src/app/supplier/SupplierComboboxField';
import { FileUploaderButton } from 'src/app/document/components/FileUploaderButton';
import { updateTrailerCost, useTrailerCost, type UpdateTrailerCostInput } from '../../models';
import { DelayedSpinner } from 'components/Spinner';

const updateTrailerCostSchema = Yup.object().shape({
  amount: Yup.string().min(1, 'Vereist').required('Vereist'),
  description: Yup.string().min(1, 'Vereist').required('Vereist'),
  date: Yup.string().required('Vereist'),
});

export const UpdateTrailerCostForm = () => {
  const { trailerCostId } = useParams<{ trailerCostId: string }>();
  invariant(trailerCostId);
  const { isLoading } = useTrailerCost(+trailerCostId);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  return <UpdateTrailerCostFormContent trailerCostId={+trailerCostId} />;
};

const UpdateTrailerCostFormContent: React.FC<{ trailerCostId: number }> = ({ trailerCostId }) => {
  const navigate = useNavigate();
  const { trailer } = useTrailer();

  // Cache-shared with the parent's useTrailerCost call above, so data is
  // already resolved by the time this component mounts.
  const { data } = useTrailerCost(trailerCostId);

  const trailerCost = nullthrows(data, 'Trailer kost niet gevonden');
  const initialValues = {
    date: trailerCost.date,
    description: trailerCost.description,
    amount: decimateAndFormatNumber(trailerCost.amount, 2),
    paidBy: PAID_BY_OPTIONS.find((v) => v.key === trailerCost.paidBy) ?? PAID_BY_OPTIONS[0],
    customer: trailerCost.customer as Customer | null,
    supplier: trailerCost.supplier as Supplier | null,
  };
  const [trailerCostAttachment, setTrailerCostAttachment] = useState(trailerCost.document || null);

  return (
    <>
      <div className="px-4">
        <div className="mb-4">
          <FileUploaderButton
            buttonText="Bijlage"
            title="Trailer kost bijlage"
            file={trailerCostAttachment}
            onSubmit={async (doc) => {
              try {
                const inputData: UpdateTrailerCostInput = {
                  documentId: doc.id,
                };

                await updateTrailerCost(trailerCost.id, inputData);
                setTrailerCostAttachment(doc);
                toast.success('Bijlage opgeslagen');
              } catch (err) {
                toast.error(`Kon bijlage niet opslaan: ${getDisplayError(err)}`);
              }
            }}
          />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={updateTrailerCostSchema}
          onSubmit={async (values) => {
            try {
              const inputData: UpdateTrailerCostInput = {
                trailerId: trailer.id,
                amount: parseNumberInput(values.amount, 2),
                date: values.date,
                description: values.description,
                paidBy: values.paidBy.key,
                customerId: values.customer?.id,
                supplierId: values.supplier?.id,
              };

              await updateTrailerCost(trailerCost.id, inputData);
              toast.success('Trailer kost aangepast');
              navigate('..');
            } catch (err: any) {
              captureException(err);
              toast.error('Kon trailer kost niet aanpassen: ' + getDisplayError(err));
            }
          }}
        >
          {({ values, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit} className="grid gap-4">
              <Card title="Algemeen">
                <InputField labelText="Datum" type="date" name="date" />
                <InputField labelText="Beschrijving" type="text" name="description" />
                <InputField labelText="Bedrag" type="number" name="amount" step="0.01" />
              </Card>

              <Card title="Betaler">
                <SimpleSelectField labelText="Betaald door" items={[...PAID_BY_OPTIONS]} name="paidBy" />
                {values.paidBy.key === TrailerCostPayee.Customer && (
                  <CustomerComboboxField name="customer" isDisabled={isSubmitting} />
                )}
                {values.paidBy.key === TrailerCostPayee.Supplier && (
                  <SupplierComboboxField name="supplier" isDisabled={isSubmitting} isNullable={true} />
                )}
              </Card>

              <div>
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  iconLeft={<PlusIcon className="button-icon" />}
                >
                  Pas trailer kost aan
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};
